// Authentication manager
import { decodeJWT } from "../utils/jwt.js";

export class AuthManager {
  constructor(config, encryptionManager, apiClient) {
    this.config = config;
    this.encryptionManager = encryptionManager;
    this.apiClient = apiClient;

    // sessionStorage key for caching tokens across SDK re-initializations
    // within the SAME TAB (SPA hot-reload, component remount, etc.).
    // sessionStorage (not localStorage) is used deliberately: the JWT is a
    // bearer credential, and sessionStorage is cleared when the tab closes,
    // shrinking the window in which an XSS attacker could steal a persisted
    // token. A fresh tab simply re-authenticates.
    // Scoped by the stable session user_id so two different users on the same
    // browser never share a token. Falls back to appId if user_id is absent.
    const scope = config.userId || config.appId || 'default';
    this.tokenCacheKey = `dhan_auth_token_${scope}`;
    // Remember which user_id this token belongs to, to validate on restore.
    this.tokenUserId = config.userId || null;

    this.authConfig = {
      token: config.authToken || null,
      refreshToken: config.refreshToken || null,
      tokenExpiry: null,
      isRefreshing: false,
      refreshQueue: [],
      autoRefresh: config.autoRefresh !== false,
    };

    // Attempt to restore a previously cached token from sessionStorage
    this.restoreCachedToken();

    this.authEndpoint = "/api/auth";
    this.initFailCount = 0;
    this.initFailTimestamp = null;
    this.maxInitAttempts = 3;
    this.initCooldownMs = 60000;
  }

  /**
   * Restores a cached access/refresh token from sessionStorage.
   * If the token is still valid (not expired), it is reused — avoiding
   * a redundant /api/auth/initialize call within the same tab.
   */
  restoreCachedToken() {
    try {
      if (typeof sessionStorage === 'undefined') return;
      const raw = sessionStorage.getItem(this.tokenCacheKey);
      if (!raw) return;

      const cached = JSON.parse(raw);
      if (!cached || !cached.token || !cached.tokenExpiry) return;

      // Reject token if it belongs to a different user_id (defensive — the key
      // is already user-scoped, but this guards against key collisions).
      if (this.tokenUserId && cached.userId && cached.userId !== this.tokenUserId) {
        sessionStorage.removeItem(this.tokenCacheKey);
        return;
      }

      // Only reuse if not expired (with 60s safety margin).
      // NOTE: the refresh token is intentionally NOT restored here — it is
      // never persisted (see cacheToken). Only the short-lived access token is
      // reused across same-tab re-inits.
      if (Date.now() < cached.tokenExpiry - 60000) {
        this.authConfig.token = cached.token;
        this.authConfig.tokenExpiry = cached.tokenExpiry;
      } else {
        // Expired — clear stale cache
        sessionStorage.removeItem(this.tokenCacheKey);
      }
    } catch (e) {
      // Corrupt cache — ignore
    }
  }

  /**
   * Persists the current ACCESS token to sessionStorage so other SDK instances
   * in the same tab can reuse it without calling the auth API again.
   *
   * The REFRESH token is deliberately NOT persisted. A refresh token can mint
   * new access tokens indefinitely, so persisting it (even in sessionStorage)
   * would let an XSS attacker maintain access well beyond the short-lived
   * access token. It is kept in memory only; a fresh tab re-authenticates.
   */
  cacheToken() {
    try {
      if (typeof sessionStorage === 'undefined') return;
      if (!this.authConfig.token || !this.authConfig.tokenExpiry) return;

      const record = {
        token: this.authConfig.token,
        tokenExpiry: this.authConfig.tokenExpiry,
        // refreshToken intentionally omitted — kept in memory only.
        userId: this.config.userId || this.tokenUserId || null,
        cachedAt: Date.now(),
      };
      sessionStorage.setItem(this.tokenCacheKey, JSON.stringify(record));
    } catch (e) {
      // sessionStorage unavailable — token works in-memory only
    }
  }

  async getValidToken() {
    if (this.isTokenValid()) {
      return this.authConfig.token;
    }

    if (this.authConfig.refreshToken) {
      try {
        const newToken = await this.refreshToken();
        if (newToken) return newToken;
      } catch (error) {
        // console.warn("Token refresh failed:", this.sanitizeForLogging(error));
        // Only nullify refresh token on authentication errors, not network errors
        if (this.isAuthenticationError(error)) {
          this.authConfig.refreshToken = null;
        }
      }
    }

    // Delay 10s to allow main project APIs to complete first
    try {
      const token = await new Promise((resolve, reject) => {
        setTimeout(async () => {
          try {
            const t = await this.initializeToken();
            if (!t) reject(new Error("Failed to initialize token"));
            else resolve(t);
          } catch (e) {
            reject(e);
          }
        }, 10000);
      });
      return token;
    } catch (error) {
      // console.error("Failed to get valid token:", this.sanitizeForLogging(error));
      throw error;
    }
  }

  isTokenValid() {
    if (!this.authConfig.token || !this.authConfig.tokenExpiry) return false;
    return Date.now() < this.authConfig.tokenExpiry;
  }

  sanitizeInput(input) {
    if (typeof input !== 'string') return input;
    return input.replace(/[<>"'&]/g, (match) => {
      const entities = { '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#x27;', '&': '&amp;' };
      return entities[match];
    });
  }

  sanitizeForLogging(error) {
    if (error && error.message) {
      return { ...error, message: this.sanitizeInput(error.message) };
    }
    return typeof error === 'string' ? this.sanitizeInput(error) : error;
  }

  isAuthenticationError(error) {
    // Check for authentication-specific errors (401, 403) vs network errors
    return error && (error.status === 401 || error.status === 403 || 
            (error.message && error.message.includes('unauthorized')));
  }

  async initializeToken() {
    if (
      this.initFailCount >= this.maxInitAttempts &&
      Date.now() - (this.initFailTimestamp || 0) < this.initCooldownMs
    ) {
      throw new Error("Max initialization attempts reached. Please try again later.");
    }
  
    if (this.authConfig.token && this.isTokenValid()) {
      return this.authConfig.token;
    }

    if (this.authConfig.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.authConfig.refreshQueue.push({ resolve, reject });
      });
    }

    try {
      this.authConfig.isRefreshing = true;

      if (this.authConfig.refreshToken) {
        const newToken = await this.refreshToken();
        this.processRefreshQueue(null, newToken);
        return newToken;
      }

      // Sanitize inputs to prevent XSS
      const sanitizedAppId = this.sanitizeInput(this.config.appId);
      const sanitizedAppKey = this.sanitizeInput(this.config.appKey);
      // Auth is scoped to the stable, session-scoped user_id only.
      // clientId is intentionally NOT sent here — it changes on login, whereas
      // user_id stays constant, so the token remains valid across guest → login.
      // The backend authenticates every activity request against this user_id.
      const sanitizedUserId = this.sanitizeInput(this.config.userId || "");

      const payload = {
        app_id: sanitizedAppId,
        secret: sanitizedAppKey,
        user_id: sanitizedUserId,
      };

      // const encryptedPayload = await this.encryptionManager.encrypt(payload);
      const response = await this.apiClient.makeRequest(
        `${this.authEndpoint}/initialize`,
        "POST",
        payload,
        false,""
      );

      if (response.access_token) {
        this.initFailCount = 0;
        this.initFailTimestamp = null;
        const accessToken = await this.encryptionManager.safeDecrypt(response.access_token);
        const refreshToken = response.refresh_token
          ? await this.encryptionManager.safeDecrypt(response.refresh_token)
          : null;

        await this.setToken(accessToken, refreshToken);
        this.processRefreshQueue(null, accessToken);
        return accessToken;
      }

      throw new Error("Invalid login response");
    } catch (error) {
      this.initFailCount++;
      this.initFailTimestamp = Date.now();
      // console.error("Authentication failed:", this.sanitizeForLogging(error));
      this.processRefreshQueue(error);
      throw error;
    } finally {
      this.authConfig.isRefreshing = false;
    }
  }

  async setToken(token, refreshToken) {
    if (!token) {
      this.authConfig.token = null;
      this.authConfig.tokenExpiry = null;
      return;
    }

    this.authConfig.token = token;
    this.authConfig.refreshToken = refreshToken || this.authConfig.refreshToken;

    try {
      const decryptedToken = await this.encryptionManager.safeDecrypt(token);
      const decoded = decodeJWT(decryptedToken);

      if (!decoded || !decoded.exp) {
        throw new Error("Invalid token: missing expiration");
      }

      this.authConfig.tokenExpiry = decoded.exp * 1000;

      // Persist token to sessionStorage so re-inits in the same tab skip the API call
      this.cacheToken();

      if (this.authConfig.autoRefresh) {
        const refreshThreshold = (this.config.refreshThreshold || 300) * 1000;
        const refreshTime = decoded.exp * 1000 - Date.now() - refreshThreshold;
        if (refreshTime > 0) {
          if (this.tokenRefreshTimeout) {
            clearTimeout(this.tokenRefreshTimeout);
          }
          this.tokenRefreshTimeout = setTimeout(
            () => this.refreshToken().catch((err) => console.error("Auto-refresh failed:", this.sanitizeForLogging(err))),
            refreshTime,
          );
        }
      }
    } catch (e) {
      // console.error("Failed to set token:", this.sanitizeForLogging(e));
      this.authConfig.token = null;
      this.authConfig.tokenExpiry = null;
      throw e;
    }
  }

  async refreshToken() {
    if (!this.authConfig.refreshToken) {
      throw new Error("No refresh token available");
    }

    if (this.authConfig.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.authConfig.refreshQueue.push({ resolve, reject });
      });
    }

    try {
      this.authConfig.isRefreshing = true;
      // const refreshTokenToUse = await this.encryptionManager.encrypt(this.authConfig.refreshToken);
      const response = await this.apiClient.makeRequest(
        `${this.authEndpoint}/refresh`,
        "POST",
        { refresh_token: this.sanitizeInput(this.authConfig.refreshToken) },
        false,
      );

      if (response && response.access_token) {
        const accessToken = await this.encryptionManager.safeDecrypt(response.access_token);
        const newRefreshToken = response.refresh_token
          ? await this.encryptionManager.safeDecrypt(response.refresh_token)
          : this.authConfig.refreshToken;

        await this.setToken(accessToken, newRefreshToken);
        this.processRefreshQueue(null, accessToken);
        return accessToken;
      }

      throw new Error("Invalid refresh token response");
    } catch (error) {
      // console.error("Token refresh failed:", this.sanitizeForLogging(error));
      this.cleanup(); // Clean up timeouts before clearing tokens
      this.authConfig.token = null;
      this.authConfig.tokenExpiry = null;
      // Only nullify refresh token on authentication errors
      if (this.isAuthenticationError(error)) {
        this.authConfig.refreshToken = null;
      }
      this.processRefreshQueue(error);
      throw error;
    } finally {
      this.authConfig.isRefreshing = false;
    }
  }

  processRefreshQueue(error, token = null) {
    while (this.authConfig.refreshQueue.length) {
      const { resolve, reject } = this.authConfig.refreshQueue.shift();
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    }
  }

  cleanup() {
    if (this.tokenRefreshTimeout) {
      clearTimeout(this.tokenRefreshTimeout);
      this.tokenRefreshTimeout = null;
    }
    // Clear cached token from sessionStorage on explicit cleanup
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(this.tokenCacheKey);
      }
    } catch (e) {
      // ignore
    }
  }
}